/**
 * DENEB London - Centralized Video Lifecycle Manager
 *
 * Handles lazy-loading, play/pause on viewport entry/exit,
 * mobile source swapping, connection-aware loading, and
 * reduced-motion preferences for all [data-video-autoplay] elements.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.innerWidth <= 900;

  // Check for slow connection or data-saver mode
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var isSlowConnection = false;
  if (conn) {
    isSlowConnection = conn.saveData || (conn.effectiveType && /2g|slow-2g/.test(conn.effectiveType));
  }

  // Module-scope singleton; created on first use and reused across all
  // initVideos() calls (DOMContentLoaded + every shopify:section:load).
  var videoObserver = null;

  function swapMobileSource(video) {
    if (!isMobile) return;
    var mobileSrc = video.getAttribute('data-mobile-src');
    var desktopSrc = video.getAttribute('data-desktop-src');
    if (mobileSrc && mobileSrc !== desktopSrc) {
      var source = video.querySelector('source');
      if (source) {
        source.src = mobileSrc;
      } else {
        video.src = mobileSrc;
      }
    }
  }

  function lazyLoadVideo(video) {
    var lazySrc = video.getAttribute('data-src');
    if (lazySrc) {
      var source = video.querySelector('source');
      if (source) {
        source.src = lazySrc;
      } else {
        video.src = lazySrc;
      }
      video.removeAttribute('data-src');
      video.load();
    }
  }

  function getObserver() {
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              // Lazy-load source if needed
              if (video.hasAttribute('data-src')) {
                lazyLoadVideo(video);
                swapMobileSource(video);
              }
              video.play().catch(function () {
                // Autoplay may be blocked by browser
              });
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.25 }
      );
    }
    return videoObserver;
  }

  function initVideos() {
    var videos = document.querySelectorAll('[data-video-autoplay]');
    if (videos.length === 0) return;

    videos.forEach(function (video) {
      // Skip videos that have already been set up on a previous initVideos() call
      // (guards against duplicate observers on shopify:section:load re-runs).
      if (video.dataset.videoObserved === 'true') return;
      video.dataset.videoObserved = 'true';

      // If reduced motion is preferred, show poster and don't autoplay
      if (prefersReducedMotion) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }

      // If slow connection, show poster with play button overlay
      if (isSlowConnection) {
        video.removeAttribute('autoplay');
        video.preload = 'none';
        video.pause();
        // Add a play button overlay
        var wrapper = video.parentElement;
        if (wrapper && !wrapper.querySelector('.video-play-btn')) {
          var btn = document.createElement('button');
          btn.className = 'video-play-btn';
          btn.setAttribute('aria-label', 'Play video');
          btn.innerHTML = '<svg width="48" height="48" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>';
          btn.addEventListener('click', function () {
            lazyLoadVideo(video);
            swapMobileSource(video);
            video.play();
            btn.remove();
          });
          wrapper.style.position = wrapper.style.position || 'relative';
          wrapper.appendChild(btn);
        }
        return;
      }

      // Normal mode: use the shared IntersectionObserver for lazy-load and play/pause
      swapMobileSource(video);
      getObserver().observe(video);
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideos);
  } else {
    initVideos();
  }

  // Re-init when new content is loaded (e.g., Shopify section rendering).
  // The videoObserved guard ensures only newly-inserted videos are processed.
  document.addEventListener('shopify:section:load', initVideos);
})();
